import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, } from 'react-native';
import { Card as BaseCard, LinearProgress, withTheme, Text, } from '@rneui/themed';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import { CardOverlay } from '@annunciate/ui/dist/components/shared/CardOverlay';
import { Text as SkeletonText } from '../skeleton';
import { useTheme } from '../../hooks';
import { Image } from './Image';
import { Hoverable } from './Hoverable';
import { htmlToPlainText } from '../../utils';
export const UnthemedCard = ({ uri, title, subtitle, description, onPress = () => { }, heightConstraint, widthConstraint, isLocked = false, isLoading = false, progress, disableMarginScaling = false, ImageComponent = Image, item, maxTitleLines = 1, cardWidthBase = 0, cardStyle, imageContainerStyle, imageStyle, containerStyle, progressColor, progressStyle, featuredTitleStyle, featuredSubtitleStyle, skeletonTextStyle, }) => {
    const { colors } = useTheme();
    const [maxWidth, setMaxWidth] = useState(0);
    const width = maxWidth + cardWidthBase;
    const animateHover = useSharedValue(0);
    const marginStyle = useAnimatedStyle(() => {
        if (disableMarginScaling) {
            return {};
        }
        const factor = animateHover.value === 0 ? 0 : 1;
        return {
            marginHorizontal: withTiming(factor * 25, { duration: 250 }),
        };
    });
    const zIndexStyle = useAnimatedStyle(() => {
        return {
            zIndex: animateHover.value === 0 ? undefined : 999,
        };
    });
    const hoverStyle = useAnimatedStyle(() => {
        return {
            transform: [
                { scale: withTiming(animateHover.value + 1, { duration: 250 }) },
                { translateZ: 0 },
            ],
            position: 'relative',
            backgroundColor: colors.backgroundPrimary,
            shadowColor: colors.backgroundInverted,
            shadowRadius: withTiming(animateHover.value === 0 ? 0 : 10, { duration: 250 }),
            shadowOpacity: withTiming(animateHover.value === 0 ? 0 : .5, { duration: 250 }),
            shadowOffset: {
                width: 0,
                height: 5,
            },
        };
    });
    const overlayBackdropStyle = useAnimatedStyle(() => {
        return {
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: colors.backgroundPrimary,
            opacity: withTiming(animateHover.value === 0 ? 0 : .7, { duration: 250 }),
        };
    });
    const overlayInfoStyle = useAnimatedStyle(() => {
        return {
            position: 'absolute',
            width: '100%',
            height: '100%',
            padding: 10,
            zIndex: 1,
            backgroundColor: 'transparent',
            opacity: withTiming(animateHover.value === 0 ? 0 : 1, { duration: 250 }),
        };
    });
    const fadeOutStyle = useAnimatedStyle(() => {
        return {
            opacity: withTiming(animateHover.value ? 0 : 1, { duration: 500 })
        };
    });
    return (React.createElement(Animated.View, { style: [
            { alignItems: 'center' },
            marginStyle,
            styles.pointerEvents,
            containerStyle,
            zIndexStyle,
        ] },
        React.createElement(Pressable, { onPress: onPress, style: { width } },
            React.createElement(Hoverable, { onHoverIn: () => animateHover.value = 0.15, onHoverOut: () => animateHover.value = 0 },
                React.createElement(BaseCard, { containerStyle: cardStyle },
                    React.createElement(Animated.View, { style: [imageContainerStyle, hoverStyle] },
                        React.createElement(Animated.View, { style: styles.overlayContainer },
                            React.createElement(Animated.View, { style: [imageStyle, overlayBackdropStyle] }),
                            React.createElement(Animated.View, { style: overlayInfoStyle },
                                React.createElement(Text, { bold: true, numberOfLines: 1, style: { marginBottom: 10 } }, title),
                                React.createElement(Text, { numberOfLines: 6 }, htmlToPlainText(description)))),
                        React.createElement(ImageComponent, { uri: uri, widthConstraint: widthConstraint, heightConstraint: heightConstraint, isLoading: isLoading, onSize: ({ width }) => setMaxWidth(width), style: imageStyle }),
                        React.createElement(CardOverlay, { item: item, isLocked: isLocked }),
                        !!progress && (React.createElement(LinearProgress, { color: progressColor, variant: "determinate", value: progress, style: progressStyle }))),
                    React.createElement(Animated.View, { style: fadeOutStyle },
                        title && !isLoading && (React.createElement(BaseCard.FeaturedTitle, { style: featuredTitleStyle, ellipsizeMode: "tail", numberOfLines: maxTitleLines }, title)),
                        subtitle && !isLoading && (React.createElement(BaseCard.FeaturedSubtitle, { style: featuredSubtitleStyle, ellipsizeMode: "tail", numberOfLines: 1 }, subtitle))),
                    isLoading && (React.createElement(SkeletonText, { width: 150, height: 18, style: skeletonTextStyle })))))));
};
export const Card = withTheme(UnthemedCard, 'CardThemed');
const styles = StyleSheet.create({
    overlayContainer: {
        position: 'absolute',
        height: '100%',
        width: '100%',
        zIndex: 1,
    },
    pointerEvents: Platform.select({
        web: {
            pointerEvents: 'initial',
        },
        default: {},
    }),
});
//# sourceMappingURL=Card.web.js.map